import React, { useMemo } from 'react';

import { MediaIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { MediaSource } from 'src/app/pages/petitionDetails/shared/types';

const MAX_VISIBLE_LOGO_COUNT = 3;

export type MediaOutletLogosProps = {
	mediaSources: readonly MediaSource[];
	testIdPrefix?: string;
};

export const MediaOutletLogos: React.FC<MediaOutletLogosProps> = ({ mediaSources, testIdPrefix = 'momentum-bar' }) => {
	const { environment } = useUtilityContext();
	const visibleSources = mediaSources.slice(0, MAX_VISIBLE_LOGO_COUNT);

	const imageHost = useMemo(() => {
		const env = environment.getEnvironment();
		switch (env) {
			case 'production':
				return 'https://assets.change.org/';
			case 'demo':
				return 'https://assets.demo.changeeng.org/';
			case 'staging':
			default:
				return 'https://assets.staging-change.org/';
		}
	}, [environment]);

	if (visibleSources.length === 0) {
		return null;
	}

	return (
		<div
			className="flex flex-row flex-wrap items-center gap-1"
			data-testid={`${testIdPrefix}-media-outlet-logos`}
			aria-hidden
		>
			{visibleSources.map((hit, index) => (
				<MediaOutletLogo
					key={hit.commentId}
					index={index}
					testIdPrefix={testIdPrefix}
					src={hit.fullSizePath}
					imageHost={imageHost}
				/>
			))}
		</div>
	);
};

type MediaOutletLogoProps = {
	index: number;
	testIdPrefix: string;
	src?: string | null;
	imageHost: string;
};

function MediaOutletLogo({ index, testIdPrefix, src, imageHost }: MediaOutletLogoProps) {
	const { translate } = useI18n();

	return (
		<div
			className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#E1DDE9] text-[#403A49] transition-transform duration-150 ease-out hover:-translate-y-1"
			data-testid={`${testIdPrefix}-media-outlet-logo-${index}`}
			title={translate('corgi.petition_details.momentum_bar.media_outlet_title')}
		>
			{src ? (
				<div>
					<img src={`${imageHost}${src}`} alt="" />
				</div>
			) : (
				<MediaIcon size="small" aria-hidden />
			)}
		</div>
	);
}
